import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Grid, 
  Paper,
  Divider,
  Container,
  useTheme,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Box
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  CreditCard as CreditCardIcon,
  Payment as PaymentIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { isAuthenticated } from '../services/authService';
import Layout from '../components/layout/Layout';
import Chart from 'react-apexcharts';
import axiosInstance from '../utils/axios';
import API_CONFIG from '../config/api.config';
import { formatCurrency } from '../utils/formatCurrency';
import { format, subDays } from 'date-fns';
import { customerService } from '../services/customerService';
import creditService from '../services/creditService';
import dashboardService from '../services/dashboardService';
import PageTitle from '../components/PageTitle';
import { analyzeSalesRevenue } from '../utils/salesRevenueDebug';

/**
 * Dashboard page component
 * @returns {JSX.Element} Dashboard page
 */
const DashboardPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('today'); // Default to today
  const [totalSales, setTotalSales] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const [totalSalesAmount, setTotalSalesAmount] = useState(0);
  const [totalCreditCustomers, setTotalCreditCustomers] = useState(0);
  const [totalDueAmount, setTotalDueAmount] = useState(0);
  const [totalProductsQuantity, setTotalProductsQuantity] = useState(0);

  // Chart data states
  const [salesChartData, setSalesChartData] = useState([35, 50, 49, 60, 70, 91]);
  const [salesChartLabels, setSalesChartLabels] = useState(['Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May']);
  const [inventoryChartData, setInventoryChartData] = useState([40, 30, 20, 10]);
  const [inventoryChartLabels, setInventoryChartLabels] = useState(['Electronics', 'Clothing', 'Food', 'Other']);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate('/');
    }
  }, [navigate]);

  // Separately fetch basic customer, supplier and product counts to improve loading time
  useEffect(() => {
    const fetchBasicCounts = async () => {
      try {
        // Fetch all customers
        const customersResponse = await customerService.getAllCustomers({
          page: 0,
          size: 1000,
          sort: 'name',
          direction: 'ASC'
        });
        const allCustomers = customersResponse.content || [];
        setTotalCustomers(allCustomers.length);

        // Fetch all suppliers
        const suppliersResponse = await axiosInstance.get('/suppliers', {
          params: { page: 0, size: 1000 }
        });
        const allSuppliers = suppliersResponse.data?.content || [];
        setTotalSuppliers(allSuppliers.length);

        // Fetch all products with pagination
        let allProducts = [];
        let page = 0;
        const pageSize = 100;
        let totalPages = 1;

        // Use a large page size to minimize requests
        do {
          const productsResponse = await axiosInstance.get('/products', {
            params: {
              page,
              size: pageSize,
              sortField: 'name',
              direction: 'asc'
            }
          });
        const products = productsResponse.data?.content || [];
          allProducts = allProducts.concat(products);
          totalPages = productsResponse.data?.totalPages || 1;
          page++;
        } while (page < totalPages);
        
        // Filter active products
        const activeProducts = allProducts.filter(product => String(product.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
        // Calculate total products quantity from active products only
        const totalQuantity = activeProducts.reduce((sum, product) => {
          const productQuantity = Number(product.quantity) || 0;
          return sum + productQuantity;
        }, 0);
        setTotalProductsQuantity(totalQuantity);

        // Debug credit customers calculation
        try {
          const debugResponse = await axiosInstance.get('/customer-credit/debug/all-credit-customers');
          console.log('🔍 Credit Customers Debug Data:', debugResponse.data);
          console.log('📊 Dashboard vs Report Comparison:');
          console.log('   Dashboard (old method): Uses credit_balance directly from customers table');
          console.log('   Report (correct method): Uses calculated balance_due from credit service');
          console.log('   Customers with credit_balance > 0:', debugResponse.data.totalCustomersWithCreditBalance);
          console.log('   Total credit_balance sum:', debugResponse.data.totalCreditBalanceSum);
          console.log('   Customers with actual balance_due > 0:', debugResponse.data.totalCustomersWithActualBalanceDue);
          console.log('   Total actual balance_due:', debugResponse.data.totalActualBalanceDue);
        } catch (debugError) {
          console.error('Debug endpoint error:', debugError);
        }

        // Calculate credit customers and due amount using the centralized dashboard service
        // This ensures consistency with the credit customers report
        try {
          const creditData = await dashboardService.getCreditCustomersData();
          setTotalCreditCustomers(creditData.totalCreditCustomers);
          setTotalDueAmount(creditData.totalDueAmount);
          
          console.log('📈 Dashboard Credit Calculation Results (using centralized service):');
          console.log('   Customers with positive balance due:', creditData.totalCreditCustomers);
          console.log('   Total balance due:', creditData.totalDueAmount);
        } catch (creditError) {
          console.error('Error fetching credit customers data:', creditError);
          // Fallback to zero values if the service fails
          setTotalCreditCustomers(0);
          setTotalDueAmount(0);
        }
      } catch (error) {
        console.error('Error fetching basic counts:', error);
      }
    };
    fetchBasicCounts();
  }, []);

  // Fetch filtered dashboard data - only for the cards that need it
  useEffect(() => {
    const fetchFilteredData = async () => {
      try {
        setLoading(true);
        // Calculate date range based on selected filter
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let startDate = new Date();
        let endDate = new Date();
        if (timeRange === 'today') {
          startDate = today;
        } else if (timeRange === 'yesterday') {
          startDate = subDays(today, 1);
          endDate = subDays(today, 1);
        } else if (timeRange === '7days') {
          startDate = subDays(today, 6); // Last 7 days including today
        } else if (timeRange === '30days') {
          startDate = subDays(today, 29); // Last 30 days including today
        }
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        
        // Fetch all sales orders for date range
        const salesResponse = await axiosInstance.get('/sales-orders/date-range', {
          params: { 
            startDate: formattedStartDate, 
            endDate: formattedEndDate
          }
        });
        
        const allSalesOrders = Array.isArray(salesResponse.data) ? salesResponse.data : [];
        
        // Filter orders to exclude cancelled and returned (same logic as sales amount calculation)
        const eligibleOrders = allSalesOrders.filter(order => {
          return order.status !== 'RETURNED' && order.status !== 'CANCELLED';
        });
        
        setTotalSales(eligibleOrders.length);
        
        // Debug sales revenue calculation
        const revenueAnalysis = analyzeSalesRevenue(allSalesOrders, getTimeRangeLabel(timeRange));
        
        // Calculate total sales amount for the filtered period
        // Filter for valid revenue-generating orders
        const filteredSalesAmount = eligibleOrders
          .reduce((total, order) => {
            const orderTotal = Number(order.total) || 0;
            return total + orderTotal;
          }, 0);
        
        console.log(`📊 Dashboard Revenue Calculation Summary:`);
        console.log(`   Time Range: ${getTimeRangeLabel(timeRange)}`);
        console.log(`   Date Range: ${formattedStartDate} to ${formattedEndDate}`);
        console.log(`   Total Orders (All): ${allSalesOrders.length}`);
        console.log(`   Revenue-Eligible Orders: ${eligibleOrders.length}`);
        console.log(`   Calculated Revenue: $${filteredSalesAmount.toLocaleString()}`);
        
        setTotalSalesAmount(filteredSalesAmount);
      } catch (error) {
        console.error('Error fetching filtered dashboard data:', error);
        console.error('Error details:', error.response ? {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        } : 'No response from server');
      } finally {
        setLoading(false);
      }
    };
    fetchFilteredData();
  }, [timeRange]);

  // Fetch chart data
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setChartLoading(true);
        
        // Fetch sales chart data (monthly sales for the last 6 months)
        try {
          const salesChartResponse = await axiosInstance.get(API_CONFIG.ENDPOINTS.DASHBOARD_SALES_CHART);
          if (salesChartResponse.data) {
            // Handle new format with both data and labels
            if (salesChartResponse.data.data && Array.isArray(salesChartResponse.data.data)) {
              setSalesChartData(salesChartResponse.data.data);
            }
            if (salesChartResponse.data.labels && Array.isArray(salesChartResponse.data.labels)) {
              setSalesChartLabels(salesChartResponse.data.labels);
            }
            console.log('Sales chart data:', salesChartResponse.data);
          }
        } catch (error) {
          console.warn('Sales chart API not available, using sample data:', error.message);
        }
        
        // Fetch inventory distribution data
        try {
          const inventoryResponse = await axiosInstance.get(API_CONFIG.ENDPOINTS.DASHBOARD_INVENTORY_CHART);
          if (inventoryResponse.data) {
            setInventoryChartData(inventoryResponse.data.values || [40, 30, 20, 10]);
            setInventoryChartLabels(inventoryResponse.data.labels || ['Electronics', 'Clothing', 'Food', 'Other']);
          }
        } catch (error) {
          console.warn('Inventory chart API not available, using sample data:', error.message);
        }
        
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setChartLoading(false);
      }
    };
    
    fetchChartData();
  }, []);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  if (!isAuthenticated()) {
    return null; // Don't render anything while redirecting
  }

  // Sample data for KPI cards
  const kpiData = [
    { 
      title: 'Total Sales Orders', 
      value: loading ? <CircularProgress size={20} /> : totalSales, 
      icon: TrendingUpIcon, 
      color: '#2196F3',
      gradient: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
      onClick: () => navigate('/reports/sales'),
      filtered: true
    },
    { 
      title: 'Active Customers', 
      value: loading ? <CircularProgress size={20} /> : totalCustomers, 
      icon: PeopleIcon, 
      color: '#9C27B0',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
      onClick: () => navigate('/sales/customers'),
      filtered: false
    },
    { 
      title: 'Active Suppliers', 
      value: loading ? <CircularProgress size={20} /> : totalSuppliers, 
      icon: LocalShippingIcon, 
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
      onClick: () => navigate('/purchases/suppliers'),
      filtered: false
    },
    { 
      title: 'Total Sales Amount', 
      value: loading ? <CircularProgress size={20} /> : '$' + totalSalesAmount.toLocaleString(), 
      icon: AttachMoneyIcon, 
      color: '#00BCD4',
      gradient: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
      onClick: () => navigate('/reports/sales'),
      filtered: true
    },
    { 
      title: 'Total Credit Customers', 
      value: loading ? <CircularProgress size={20} /> : totalCreditCustomers, 
      icon: CreditCardIcon, 
      color: '#673AB7',
      gradient: 'linear-gradient(135deg, #673AB7 0%, #512DA8 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
      onClick: () => navigate('/reports/credit-customers'),
      filtered: false
    },
    { 
      title: 'Total Due Amount', 
      value: loading ? <CircularProgress size={20} /> : '$' + totalDueAmount.toLocaleString(), 
      icon: PaymentIcon, 
      color: '#F44336',
      gradient: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
      onClick: () => navigate('/reports/credit-customers'),
      filtered: false
    },
    { 
      title: 'Total Products Quantity', 
      value: loading ? <CircularProgress size={20} /> : totalProductsQuantity.toLocaleString(), 
      icon: InventoryIcon, 
      color: '#8BC34A',
      gradient: 'linear-gradient(135deg, #8BC34A 0%, #689F38 100%)',
      textColor: '#ffffff',
      iconBg: 'rgba(255, 255, 255, 0.2)',
      onClick: () => navigate('/inventory/products'),
      filtered: false
    }
  ];

  // Sample data for sales chart
  const salesChartOptions = {
    chart: {
      id: 'sales-chart',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      background: 'transparent',
      fontFamily: theme.typography.fontFamily,
    },
    colors: ['#2196F3', '#4CAF50', '#FF9800'],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
      lineCap: 'round',
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    },
    xaxis: {
      categories: salesChartLabels,
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          fontSize: '12px',
          fontFamily: theme.typography.fontFamily,
        },
      },
      axisBorder: {
        show: true,
        color: theme.palette.divider,
      },
      axisTicks: {
        show: true,
        color: theme.palette.divider,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          fontSize: '12px',
          fontFamily: theme.typography.fontFamily,
        },
        formatter: (value) => `$${value.toLocaleString()}`
      },
      title: {
        text: 'Sales Amount',
        style: {
          color: theme.palette.text.secondary,
          fontSize: '12px',
          fontFamily: theme.typography.fontFamily,
        }
      }
    },
    tooltip: {
      theme: theme.palette.mode,
      x: {
        show: true,
        formatter: (value) => `Month: ${value}`
      },
      y: {
        formatter: (value) => `$${value.toLocaleString()}`
      },
      style: {
        fontSize: '12px',
        fontFamily: theme.typography.fontFamily,
      },
      marker: {
        show: true,
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      labels: {
        colors: theme.palette.text.primary,
      },
      markers: {
        width: 12,
        height: 12,
        strokeWidth: 0,
        strokeColor: '#fff',
        radius: 12,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    markers: {
      size: 5,
      strokeWidth: 0,
      hover: {
        size: 7
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 0.8,
        opacityTo: 0.2,
        stops: [0, 100]
      }
    }
  };

  const salesChartSeries = [
    {
      name: 'Sales',
      data: salesChartData,
    },
  ];

  // Process inventory chart data to ensure proper percentage calculation
  const processInventoryChartData = (data, labels) => {
    try {
      if (!Array.isArray(data) || data.length === 0) {
        console.warn('Inventory chart data is not a valid array, using fallback data');
        return { processedData: [40, 30, 20, 10], processedLabels: ['Electronics', 'Clothing', 'Food', 'Other'] };
      }
      
      // Calculate total for percentage calculation
      const total = data.reduce((sum, value) => {
        const numValue = parseInt(value) || 0;
        return sum + Math.max(0, numValue); // Ensure no negative values
      }, 0);
      
      if (total === 0) {
        console.warn('Total inventory is 0, using fallback data');
        return { processedData: [40, 30, 20, 10], processedLabels: ['Electronics', 'Clothing', 'Food', 'Other'] };
      }
      
      // Ensure we have valid data - filter out negative or invalid values
      const processedData = data.map(value => {
        const numValue = parseInt(value) || 0;
        return Math.max(0, numValue); // Ensure no negative values
      }).filter(value => value >= 0); // Filter out any remaining invalid values
      
      // Ensure we have matching labels
      const processedLabels = labels && Array.isArray(labels) && labels.length === data.length ? 
        labels.slice(0, processedData.length) : 
        processedData.map((_, index) => `Category ${index + 1}`);
      
      // Final validation - ensure we have at least some data
      if (processedData.length === 0 || processedData.every(val => val === 0)) {
        console.warn('No valid inventory data found, using fallback data');
        return { processedData: [40, 30, 20, 10], processedLabels: ['Electronics', 'Clothing', 'Food', 'Other'] };
      }
      
      console.log('📊 Pie Chart Data Processing:');
      console.log('   Original data:', data);
      console.log('   Processed data:', processedData);
      console.log('   Labels:', processedLabels);
      console.log('   Total quantity:', total);
      
      return { processedData, processedLabels };
    } catch (error) {
      console.error('Error processing inventory chart data:', error);
      return { processedData: [40, 30, 20, 10], processedLabels: ['Electronics', 'Clothing', 'Food', 'Other'] };
    }
  };

  // Get processed chart data
  const { processedData: finalInventoryData, processedLabels: finalInventoryLabels } = 
    processInventoryChartData(inventoryChartData, inventoryChartLabels);

  // Sample data for inventory chart
  const inventoryChartOptions = {
    chart: {
      id: 'inventory-chart',
      toolbar: {
        show: false,
      },
      background: 'transparent',
      fontFamily: theme.typography.fontFamily,
    },
    colors: [
      '#2196F3',
      '#4CAF50',
      '#FF9800',
      '#9C27B0',
      '#F44336',
      '#00BCD4'
    ],
    labels: finalInventoryLabels,
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      labels: {
        colors: theme.palette.text.primary,
        useSeriesColors: false
      },
      markers: {
        width: 12,
        height: 12,
        strokeWidth: 0,
        strokeColor: '#fff',
        radius: 12,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        try {
          // Enhanced formatter with better percentage calculation
          const percentage = typeof val === 'number' ? val : parseFloat(val);
          if (isNaN(percentage) || percentage === null || percentage === undefined) return '0%';
          
          // Show percentage with 1 decimal place for values > 1%, no decimals for smaller values
          if (percentage >= 1) {
            return percentage.toFixed(1) + '%';
          } else if (percentage > 0) {
            return percentage.toFixed(1) + '%';
          } else {
            return '0%';
          }
        } catch (error) {
          console.warn('Error in pie chart dataLabels formatter:', error);
          return '0%';
        }
      },
      style: {
        fontSize: '14px',
        fontFamily: theme.typography.fontFamily,
        fontWeight: 600,
        colors: ['#fff']
      },
      background: {
        enabled: true,
        foreColor: '#000',
        borderRadius: 4,
        padding: 4,
        opacity: 0.8
      },
      dropShadow: {
        enabled: true,
        opacity: 0.5,
        blur: 3,
        left: 2,
        top: 2
      }
    },
    tooltip: {
      theme: theme.palette.mode,
      y: {
        formatter: function(value, { series, seriesIndex, dataPointIndex, w }) {
          // Add error handling for series
          if (!series || !Array.isArray(series) || series.length === 0) {
            return `${value.toLocaleString()} items`;
          }
          
          try {
            // Calculate percentage from the actual data
            const total = series.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
            const percentage = total > 0 ? (value / total) * 100 : 0;
            return `${value.toLocaleString()} items (${percentage.toFixed(1)}%)`;
          } catch (error) {
            console.warn('Error in pie chart tooltip formatter:', error);
            return `${value.toLocaleString()} items`;
          }
        }
      },
      style: {
        fontSize: '12px',
        fontFamily: theme.typography.fontFamily,
      },
      marker: {
        show: true,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '0%',
          labels: {
            show: false
          }
        },
        customScale: 0.85,
        expandOnClick: true,
        dataLabels: {
          offset: 20,
          minAngleToShowLabel: 5 // Only show labels for slices > 5 degrees
        }
      }
    },
    stroke: {
      width: 3,
      colors: ['#fff']
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.15
        }
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'darken',
          value: 0.7
        }
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 320
        },
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          style: {
            fontSize: '12px'
          }
        }
      }
    }]
  };

  const inventoryChartSeries = finalInventoryData;

  // Get filter label for the time range
  const getTimeRangeLabel = (range) => {
    switch(range) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case '7days': return 'Last 7 Days';
      case '30days': return 'Last 30 Days';
      default: return 'Today';
    }
  };

  return (
    <Layout>
      <PageTitle title="Dashboard" />
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Dashboard</Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={timeRange}
              onChange={handleTimeRangeChange}
              displayEmpty
              sx={{ backgroundColor: 'white' }}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="7days">Last 7 Days</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Grid container spacing={3}>
          {kpiData.map((kpi, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2,
                  height: '100%',
                  background: kpi.gradient,
                  border: 'none',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: kpi.onClick ? 'pointer' : 'default',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                    '& .card-icon': {
                      transform: 'scale(1.1)',
                      opacity: 1
                    },
                    '& .card-content': {
                      transform: 'translateY(-5px)'
                    }
                  }
                }}
                onClick={kpi.onClick}
              >
                <Box 
                  className="card-content"
                  sx={{ 
                    transition: 'transform 0.3s ease',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          color: '#ffffff',
                          fontWeight: 500,
                          mb: 0.5,
                          opacity: 0.9
                        }}
                      >
                        {kpi.title}
                        {kpi.filtered && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block', 
                              color: '#ffffff',
                              mt: 0.5,
                              opacity: 0.8
                            }}
                          >
                            {getTimeRangeLabel(timeRange)}
                          </Typography>
                        )}
                      </Typography>
                      <Typography 
                        variant="h5" 
                        component="div" 
                        sx={{ 
                          mb: 1,
                          fontWeight: 600,
                          color: '#ffffff'
                        }}
                      >
                        {kpi.value}
                      </Typography>
                      {kpi.onClick && (
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            color: '#ffffff',
                            fontSize: '0.75rem',
                            mt: 1,
                            fontWeight: 'medium',
                            opacity: 0.8,
                            transition: 'opacity 0.3s ease',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#ffffff', 
                              fontWeight: 500, 
                              mr: 0.5 
                            }}
                          >
                            View Details
                          </Typography>
                          <ArrowForwardIcon sx={{ fontSize: 12 }} />
                        </Box>
                      )}
                    </Box>
                    <Box 
                      className="card-icon"
                      sx={{ 
                        background: 'rgba(255, 255, 255, 0.2)',
                        p: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        opacity: 0.9,
                        backdropFilter: 'blur(4px)'
                      }}
                    >
                      <kpi.icon sx={{ color: '#ffffff', fontSize: 28 }} />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Sales Trend Chart */}
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: '100%',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <TrendingUpIcon color="primary" />
                Sales Trend
                {chartLoading && <CircularProgress size={16} />}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Chart
                options={salesChartOptions}
                series={salesChartSeries}
                type="line"
                height={350}
              />
            </Paper>
          </Grid>
          
          {/* Inventory Distribution Chart */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: '100%',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <InventoryIcon color="primary" />
                Inventory Distribution
                {chartLoading && <CircularProgress size={16} />}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Chart
                options={inventoryChartOptions}
                series={inventoryChartSeries}
                type="pie"
                height={350}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default DashboardPage;